import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";

const ADJUSTMENT_TYPES = [
  { id: "damage", name: "Damage" },
  { id: "loss", name: "Loss" },
  { id: "correction", name: "Correction" },
  { id: "write_off", name: "Write Off" },
  { id: "found", name: "Found" },
  { id: "return", name: "Return" },
];

const NewAdjustment = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [item, setItem] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("correction");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItemInfo, setSelectedItemInfo] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchInventoryItems();
  }, []);

  useEffect(() => {
    if (item) {
      const found = inventoryItems.find((i) => i._id === item);
      setSelectedItemInfo(found || null);
    } else {
      setSelectedItemInfo(null);
    }
  }, [item, inventoryItems]);

  const fetchInventoryItems = async () => {
    try {
      const response = await fetch("/api/getAllInventoryItems?limit=200", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setInventoryItems(data.items || []);
      }
    } catch (err) {
      setErrorMessage("Failed to load inventory items");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const validateForm = () => {
    if (!item) return "Please select an item";
    if (!adjustmentType) return "Please select an adjustment type";
    if (!quantity || parseInt(quantity) === 0) return "Please enter a valid quantity";
    if (!reason.trim()) return "Reason is required";
    if (!date) return "Please select a date";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch("/api/addStockAdjustment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          item,
          adjustmentType,
          quantity: parseInt(quantity),
          reason: reason.trim(),
          date,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error || "Failed to create adjustment");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      setSuccessMessage("Stock adjustment created successfully");
      setTimeout(() => {
        navigate("/Adjustments");
      }, 1500);
    } catch (err) {
      setErrorMessage("Failed to create stock adjustment");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const isAdditive = ["found", "return", "correction"].includes(adjustmentType);

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/Adjustments")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">New Stock Adjustment</h2>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Item */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Item <span className="text-red-500">*</span>
            </label>
            <select
              value={item}
              onChange={(e) => setItem(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Item</option>
              {inventoryItems.map((inv) => (
                <option key={inv._id} value={inv._id}>
                  {inv.sku} - {inv.name}
                </option>
              ))}
            </select>
          </div>

          {/* Adjustment Type */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Adjustment Type <span className="text-red-500">*</span>
            </label>
            <select
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ADJUSTMENT_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Current Stock Info */}
        {selectedItemInfo && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Item Name</p>
                <p className="font-medium">{selectedItemInfo.name}</p>
              </div>
              <div>
                <p className="text-gray-500">SKU</p>
                <p className="font-medium">{selectedItemInfo.sku}</p>
              </div>
              <div>
                <p className="text-gray-500">Current Stock</p>
                <p className="font-semibold text-primary">{selectedItemInfo.currentStock}</p>
              </div>
              <div>
                <p className="text-gray-500">After Adjustment</p>
                <p className={`font-semibold ${
                  isAdditive ? "text-green-600" : "text-red-600"
                }`}>
                  {quantity
                    ? isAdditive
                      ? selectedItemInfo.currentStock + parseInt(quantity || 0)
                      : selectedItemInfo.currentStock - parseInt(quantity || 0)
                    : selectedItemInfo.currentStock}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Explain the reason for this stock adjustment"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/Adjustments")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Create Adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewAdjustment;
